<template>
  <div class="image-picker">
    <!-- Selected Images Preview -->
    <div v-if="selectedImages.length > 0" class="selected-images mb-4">
      <label class="form-label">
        {{ $t('BUSINESS_DATA.FAQS.SELECTED_IMAGES') }} ({{ selectedImages.length }})
      </label>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="(img, index) in selectedImages"
          :key="img.id"
          class="relative group"
        >
          <div class="w-16 h-16 rounded-lg overflow-hidden border-2 border-blue-500">
            <img :src="img.thumb_url || img.file_url" class="w-full h-full object-cover" />
          </div>
          <button
            @click="removeImage(index)"
            class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
          >
            ×
          </button>
          <div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
            {{ img.title }}
          </div>
        </div>
      </div>
    </div>

    <!-- Add Images Button -->
    <div class="form-row">
      <label class="form-label">
        {{ $t('BUSINESS_DATA.FAQS.ATTACH_IMAGES') }}
      </label>
      <woot-button
        type="button"
        variant="clear"
        size="small"
        @click="showPicker = true"
      >
        <fluent-icon icon="add" size="16" class="mr-1" />
        {{ $t('BUSINESS_DATA.FAQS.SELECT_IMAGES') }}
      </woot-button>
      <div class="form-help">
        {{ $t('BUSINESS_DATA.FAQS.IMAGES_HELP') }}
      </div>
    </div>

    <!-- Image Picker Modal -->
    <woot-modal :show="showPicker" :on-close="closePicker">
      <div class="image-picker-modal">
        <div class="modal-header">
          <h2 class="text-lg font-medium text-slate-900 dark:!text-slate-100">
            {{ $t('BUSINESS_DATA.FAQS.SELECT_IMAGES') }}
          </h2>
        </div>

        <!-- Search and Filter -->
        <div class="flex items-center gap-3 mb-4">
          <woot-input
            v-model="searchQuery"
            :placeholder="$t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_SEARCH_PLACEHOLDER')"
            class="flex-1"
            @input="onSearch"
          />
          <woot-dropdown v-model="sortBy" :options="sortOptions" />
        </div>

        <!-- Breadcrumb -->
        <div v-if="currentLibrary" class="text-sm text-gray-600 mb-4">
          <button @click="goToLibraries" class="text-indigo-600 hover:underline">
            {{ $t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_TITLE') }}
          </button>
          <span class="mx-1">›</span>
          <span>{{ currentLibrary.name }}</span>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="py-6 flex items-center justify-center">
          <Spinner size="md" color-scheme="primary" />
        </div>

        <!-- Empty State -->
        <div v-else-if="!filteredItems.length" class="text-center text-gray-500 py-16">
          <p>{{ $t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_EMPTY_ITEMS') }}</p>
        </div>

        <!-- Image Grid -->
        <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
          <!-- Library Folders -->
          <div
            v-if="!currentLibrary"
            v-for="item in filteredItems"
            :key="item.id"
            class="relative group cursor-pointer"
            @click="toggleImageSelection(item)"
          >
            <div class="aspect-square w-full overflow-hidden rounded-lg border-2 transition-all bg-gray-50 flex items-center justify-center"
                 :class="isSelected(item) ? 'border-indigo-600 border-4 bg-indigo-50 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'">
              <fluent-icon icon="folder" size="48" class="text-purple-500" />
            </div>
            
            <!-- Selection Indicator -->
            <div v-if="isSelected(item)" class="absolute top-2 right-2 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm shadow-lg">
              ✓
            </div>
            
            <!-- Folder Info -->
            <div class="mt-2">
              <div class="font-medium text-sm text-gray-900 truncate">{{ item.name }}</div>
              <div class="text-xs text-gray-500">{{ item.image_items_count || 0 }} {{ $t('BUSINESS_DATA.FAQS.IMAGES_COUNT') }}</div>
            </div>
          </div>
          
          <!-- Image Items -->
          <div
            v-else
            v-for="item in filteredItems"
            :key="`image-${item.id}`"
            class="relative group cursor-pointer"
            @click="toggleImageSelection(item)"
          >
            <div class="aspect-square w-full overflow-hidden rounded-lg border-2 transition-colors"
                 :class="isSelected(item) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'">
              <img :src="item.thumb_url || item.file_url" class="w-full h-full object-cover" />
            </div>
            
            <!-- Selection Indicator -->
            <div v-if="isSelected(item)" class="absolute top-2 right-2 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm shadow-lg">
              ✓
            </div>
            
            <!-- Image Info -->
            <div class="mt-2">
              <div class="font-medium text-sm text-gray-900 truncate">{{ item.title }}</div>
              <div v-if="item.description" class="text-xs text-gray-600 truncate">{{ item.description }}</div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-4 flex justify-center">
          <woot-pagination
            :current-page="currentPage"
            :total-items="totalItems"
            :per-page="itemsPerPage"
            @page-change="onPageChange"
          />
        </div>

        <!-- Actions -->
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click="closePicker"
          >
            {{ $t('COMMON.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="button"
            color-scheme="primary"
            @click="confirmSelection"
          >
            {{ $t('BUSINESS_DATA.FAQS.CONFIRM') }} ({{ tempSelectedImages.length }})
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
export default {
  name: 'ImagePicker',
  components: { Spinner },
  
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    accountId: {
      type: [String, Number],
      required: true
    }
  },
  
  emits: ['update:modelValue'],
  
  data() {
    return {
      showPicker: false,
      searchQuery: '',
      sortBy: 'created_desc',
      sortOptions: [
        { value: 'name_asc', label: this.$t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_SORT_NAME_ASC') },
        { value: 'created_desc', label: this.$t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_SORT_NEWEST') },
        { value: 'order_asc', label: this.$t('BUSINESS_DATA.FAQS.IMAGE_LIBRARY_SORT_CUSTOM') }
      ],
      isLoading: false,
      currentLibrary: null,
      libraries: [],
      imageItems: [],
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 12,
      tempSelectedImages: [],
      cachedImages: []
    }
  },
  
  computed: {
    selectedImages() {
      // Convert IDs back to image objects for display
      if (!this.modelValue || !this.modelValue.length) return []
      // We'll need to load these from the API or store them
      return this.cachedImages.filter(img => this.modelValue.includes(img.id))
    },
    
    filteredItems() {
      if (this.currentLibrary) {
        return this.imageItems
      }
      return this.libraries
    },
    
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    }
  },
  
  watch: {
    showPicker(newVal) {
      if (newVal) {
        this.tempSelectedImages = [...this.selectedImages]
        this.loadLibraries()
      }
    },
    
    modelValue: {
      immediate: true,
      handler() {
        this.loadCachedImages()
      }
    }
  },
  
  methods: {
    async loadCachedImages() {
      if (!this.modelValue || !this.modelValue.length) {
        this.cachedImages = []
        return
      }
      
      try {
        // Load all image items by IDs from all libraries
        const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries`, {
          params: {
            per_page: 100,
            include_items: true
          }
        })
        
        // Filter items by IDs
        const allItems = []
        data.image_libraries?.forEach(library => {
          if (library.image_items) {
            allItems.push(...library.image_items)
          }
        })
        
        this.cachedImages = allItems.filter(item => this.modelValue.includes(item.id))
      } catch (error) {
        console.error('Error loading cached images:', error)
        this.cachedImages = []
      }
    },
    
    async loadLibraries() {
      if (this.currentLibrary) return
      
      this.isLoading = true
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries`, {
          params: {
            page: this.currentPage,
            per_page: this.itemsPerPage,
            q: this.searchQuery,
            sort: this.sortBy,
            active_only: 'true'
          }
        })
        this.libraries = data.image_libraries || []
        this.totalItems = data.meta?.total_count || 0
      } catch (error) {
        console.error('Error loading libraries:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    async loadImageItems(libraryId) {
      this.isLoading = true
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries/${libraryId}/image_items/by_library`, {
          params: {
            page: this.currentPage,
            per_page: this.itemsPerPage,
            search: this.searchQuery,
            sort: this.sortBy,
            active_only: 'true'
          }
        })
        this.imageItems = data.image_items || []
        this.totalItems = data.meta?.total_count || 0
        this.currentLibrary = data.library
      } catch (error) {
        console.error('Error loading image items:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    goToLibraries() {
      this.currentLibrary = null
      this.currentPage = 1
      this.loadLibraries()
    },
    
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        if (this.currentLibrary) {
          this.loadImageItems(this.currentLibrary.id)
        } else {
          this.loadLibraries()
        }
      }, 300)
    },
    
    onPageChange(page) {
      this.currentPage = page
      if (this.currentLibrary) {
        this.loadImageItems(this.currentLibrary.id)
      } else {
        this.loadLibraries()
      }
    },
    
    toggleImageSelection(item) {
      if (this.currentLibrary) {
        // Selecting image items
        const index = this.tempSelectedImages.findIndex(img => img.id === item.id)
        if (index > -1) {
          this.tempSelectedImages.splice(index, 1)
        } else {
          this.tempSelectedImages.push(item)
        }
      } else {
        // Clicking on library - navigate to items
        this.loadImageItems(item.id)
      }
    },
    
    isSelected(item) {
      return this.tempSelectedImages.some(img => img.id === item.id)
    },
    
    removeImage(index) {
      const newSelected = [...this.modelValue]
      newSelected.splice(index, 1)
      this.$emit('update:modelValue', newSelected)
    },
    
    confirmSelection() {
      // Convert image objects to IDs for the model
      const imageIds = this.tempSelectedImages.map(img => img.id)
      this.$emit('update:modelValue', imageIds)
      this.closePicker()
    },
    
    closePicker() {
      this.showPicker = false
      this.tempSelectedImages = []
      this.currentLibrary = null
      this.searchQuery = ''
      this.currentPage = 1
    }
  }
}
</script>

<style lang="scss" scoped>
.image-picker-modal {
  width: 100%;
  max-width: 800px;
  padding: 24px;
}

.modal-header {
  margin-bottom: var(--space-large);
  padding-top: 8px;
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

.selected-images {
  .form-label {
    display: block;
    margin-bottom: var(--space-smaller);
    font-weight: var(--font-weight-medium);
    color: var(--color-heading);
  }
}
</style>
